import type {
  AdminRuleVersionGetResponse,
  AdminRuleVersionsResponse,
  AdminRulesGetResponse,
  AdminStatsResponse,
  DecisionAudit,
  MasterItemsPage,
  MasterList,
  MasterTemplate,
  RuleSet,
} from '@kkp-fraud/shared';
import bundledRules from '../../../../rules/rules.v1.json';
import type { AuditEntry } from '../audit';
import { collectRange, pageOfRows, type DecisionRange, type DecisionsRangeResult, type OnRangePage } from '../decisionsRange';
import { demoAudit, mergeAudit } from './audit';
import { demoDecisions, demoStats } from './decisions';
import { DEMO_TEMPLATE_ETAG, demoMaster, demoMasterCsv, demoMasterItems, demoMasters, isDemoMasterId, mergeTemplates } from './masters';
import { isDemoProposalId } from './proposals';
// R9 cases
import type { Case, CaseFilters, CasePage, InvestigatePage, RulePrecisionReport } from '../cases';
import { demoCases, demoTimeline, isDemoCaseId, mergeCasePage, mergePrecision } from './cases';
import { demoVersions, isDemoVersionName, mergeVersions, type DemoVersion } from './versions';

export { demoDependencies } from './monitor';

/**
 * The demo data layer — `VITE_DEMO=1` (`npm run dev:admin:demo`).
 *
 * Read-only screens get realistic, deterministic fixtures where the platform has no data yet
 * (decisions, stats) and a history in front of the real one where it is thin (versions,
 * the access trail). Everything that WRITES stays real: rules GET, publish, rollback, sign-in.
 * Wave 2-0: the demo runs without a rule engine behind it, so the console does not check rules
 * in demo mode (`useRuleCheck` reports `demo` and never calls validate — publishing is still
 * refused server-side if the document is wrong), and the dry run is not offered. `api.ts` is
 * the one place this is wired in; nothing here fetches on its own.
 *
 * Demo proposals (`./proposals`) are no longer merged into anything: maker-checker is
 * dropped for now, so the console renders no proposals at all. The fixtures stay for when
 * the flow returns.
 *
 * Off by default, and off under vitest, so every other test sees the real client.
 */
export const DEMO: boolean = import.meta.env?.VITE_DEMO === '1';

/** A version that exists only in the demo history — it cannot be activated or restored. */
export function isDemoVersion(version: string): boolean {
  return DEMO && isDemoVersionName(version);
}

/** A demo master list — shown, exported, never written (the screen offers no writes on it). */
export function isDemoMaster(id: string): boolean {
  return DEMO && isDemoMasterId(id);
}

/** R11: a demo copy of a built-in template — shown, never written. */
export function isDemoTemplate(t: Pick<MasterTemplate, '_etag'>): boolean {
  return DEMO && t._etag === DEMO_TEMPLATE_ETAG;
}

// R9 cases
/** A demo case — shown, never written (the screen offers no writes on it). */
export function isDemoCase(id: string): boolean {
  return DEMO && isDemoCaseId(id);
}

/** A settled demo proposal — history, with no actions. (Only the unrendered proposal screen asks.) */
export function isDemoProposal(id: string): boolean {
  return DEMO && isDemoProposalId(id);
}

/** rules/rules.v1.json — only used when the live document cannot be read. */
export const BUNDLED_RULES = bundledRules as unknown as RuleSet;

/** The slice of the client the demo layer reads through. Settings are passed straight on. */
export interface DemoableApi<S> {
  getRules: (s: S) => Promise<AdminRulesGetResponse>;
  decisions: (s: S, limit?: number) => Promise<DecisionAudit[]>;
  decisionsRange: (s: S, range: DecisionRange, onPage?: OnRangePage) => Promise<DecisionsRangeResult>;
  stats: (s: S) => Promise<AdminStatsResponse>;
  versions: (s: S) => Promise<AdminRuleVersionsResponse>;
  version: (s: S, version: string) => Promise<AdminRuleVersionGetResponse>;
  audit: (s: S, limit?: number) => Promise<AuditEntry[]>;
  masters: {
    list: (s: S) => Promise<MasterList[]>;
    get: (s: S, id: string) => Promise<MasterList>;
    items: (s: S, id: string, q?: { search?: string; limit?: number; cursor?: string }) => Promise<MasterItemsPage>;
    exportCsv: (s: S, id: string) => Promise<Blob>;
    // R11 master templates
    templates: {
      list: (s: S) => Promise<MasterTemplate[]>;
      get: (s: S, id: string) => Promise<MasterTemplate>;
    };
  };
  // R9 cases
  cases: {
    decisions: (s: S, cardId: string, cursor?: string) => Promise<InvestigatePage>;
    list: (s: S, filters?: CaseFilters, cursor?: string) => Promise<CasePage>;
    get: (s: S, cardId: string, id: string) => Promise<Case>;
    precision: (s: S, range?: { from?: string; to?: string }) => Promise<RulePrecisionReport>;
  };
}

const CACHE_MS = 60_000;

/** 2c rule sets: settings that address one set's routes (`ApiSettings.ruleSetId`) — not the primary's. */
function scopedToASet(s: unknown): boolean {
  return typeof s === 'object' && s !== null && typeof (s as { ruleSetId?: unknown }).ruleSetId === 'string';
}

function warn(what: string, e: unknown): void {
  console.warn(`[demo] ${what} failed upstream; showing demo data only.`, e);
}

/**
 * Wrap the real client. Only the read endpoints below are replaced; every other member of
 * `real` — including every write, and `real.proposals` — is passed through untouched.
 */
export function withDemo<S, A extends DemoableApi<S>>(real: A, clock: () => number = Date.now): A {
  let active: { at: number; doc: Promise<RuleSet> } | null = null;
  /** The live document, re-read at most once a minute; the bundled one if it cannot be read. */
  const activeDoc = (s: S): Promise<RuleSet> => {
    if (active && clock() - active.at < CACHE_MS) return active.doc;
    const doc = real.getRules(s).then(
      (r) => r.ruleSet,
      (e) => {
        warn('GET rules', e);
        return BUNDLED_RULES;
      },
    );
    active = { at: clock(), doc };
    return doc;
  };

  let demoHistory: { key: string; versions: Promise<DemoVersion[]> } | null = null;
  /**
   * The real release list (or [] if it failed) and the demo releases that precede it. Shared
   * for a few seconds, so a rule's version history — one `version()` per release — reads the
   * real list once, not once per row.
   */
  let recent: { at: number; value: ReturnType<typeof loadHistory> } | null = null;
  const history = (s: S) => {
    if (!recent || clock() - recent.at > 5_000) recent = { at: clock(), value: loadHistory(s) };
    return recent.value;
  };
  const loadHistory = async (s: S) => {
    let realVersions: AdminRuleVersionsResponse['versions'] = [];
    try {
      realVersions = (await real.versions(s)).versions;
    } catch (e) {
      warn('GET versions', e);
    }
    const oldest = [...realVersions].sort((a, b) => Date.parse(a.savedUtc) - Date.parse(b.savedUtc))[0] ?? null;
    const key = oldest ? `${oldest.version}|${oldest.savedUtc}` : 'none';
    if (demoHistory?.key !== key) {
      const versions = (async () => {
        let base: RuleSet;
        try {
          base = oldest ? (await real.version(s, oldest.version)).ruleSet : await activeDoc(s);
        } catch (e) {
          warn('GET oldest version', e);
          base = await activeDoc(s);
        }
        return demoVersions(base, oldest?.savedUtc ?? null, clock());
      })();
      demoHistory = { key, versions };
    }
    return { realVersions, demo: await demoHistory.versions };
  };

  const rows = async (s: S) => demoDecisions({ ruleSet: await activeDoc(s), now: clock() });

  const wrapped: DemoableApi<S> = {
    getRules: real.getRules,
    decisions: async (s, limit = 100) => (await rows(s)).slice(0, Math.max(0, limit)),
    // The same paging loop as the real client, over the fixtures. They cover the last 24
    // hours only, so a period before that (last month) is honestly empty in the demo.
    decisionsRange: async (s, range, onPage) => {
      const all = await rows(s);
      return collectRange(async (q) => pageOfRows(all, q), range, onPage);
    },
    stats: async (s) => demoStats(await rows(s)),
    versions: async (s) => {
      // 2c rule sets: the demo history is the primary's; another set's list is its own.
      if (scopedToASet(s)) return real.versions(s);
      const { realVersions, demo } = await history(s);
      return { versions: mergeVersions(realVersions, demo) };
    },
    version: async (s, version) => {
      if (!scopedToASet(s) && isDemoVersionName(version)) {
        const { realVersions, demo } = await history(s);
        const hit = demo.find((d) => d.summary.version === version);
        if (hit && !realVersions.some((v) => v.version === version)) return structuredClone(hit.detail);
      }
      return real.version(s, version);
    },
    audit: async (s, limit = 200) => {
      let realEntries: AuditEntry[] = [];
      try {
        realEntries = await real.audit(s, limit);
      } catch (e) {
        warn('GET audit', e);
      }
      return mergeAudit(realEntries, demoAudit(clock()), limit);
    },
    // The three demo lists in front of the real ones; reads of a demo list answer from the
    // fixtures. Every write (`real.masters.*`) goes to the admin service untouched.
    masters: {
      ...real.masters,
      list: async (s) => {
        let realLists: MasterList[] = [];
        try {
          realLists = await real.masters.list(s);
        } catch (e) {
          warn('GET masters', e);
        }
        return [...demoMasters(clock()), ...realLists.filter((l) => !isDemoMasterId(l.id))].sort((a, b) => (a.id < b.id ? -1 : 1));
      },
      get: async (s, id) => {
        const demo = isDemoMasterId(id) ? demoMaster(id, clock()) : undefined;
        return demo ? structuredClone(demo) : real.masters.get(s, id);
      },
      items: async (s, id, q = {}) =>
        isDemoMasterId(id) && demoMaster(id, clock()) ? demoMasterItems(id, q, clock()) : real.masters.items(s, id, q),
      exportCsv: async (s, id) =>
        isDemoMasterId(id) && demoMaster(id, clock()) ? demoMasterCsv(id, clock()) : real.masters.exportCsv(s, id),
      // R11: the two built-in templates behind the real ones, so the demo lists have theirs.
      templates: {
        ...real.masters.templates,
        list: async (s) => {
          let realTemplates: MasterTemplate[] = [];
          try {
            realTemplates = await real.masters.templates.list(s);
          } catch (e) {
            warn('GET master templates', e);
          }
          return mergeTemplates(realTemplates, clock());
        },
        get: async (s, id) => {
          try {
            return await real.masters.templates.get(s, id);
          } catch (e) {
            const demo = mergeTemplates([], clock()).find((t) => t.id === id);
            if (demo) return demo;
            throw e;
          }
        },
      },
    },
    // R9 cases — the demo cases in front of the real ones; a card with demo decisions answers
    // from the feed. Every write (`real.cases.*`) goes to the admin service untouched.
    cases: {
      ...real.cases,
      decisions: async (s, cardId, cursor) => {
        const all = await rows(s);
        if (!cursor && all.some((d) => d.cardId === cardId)) return demoTimeline(all, demoCases(all), cardId);
        return real.cases.decisions(s, cardId, cursor);
      },
      list: async (s, filters = {}, cursor) => {
        let realPage: CasePage = { items: [] };
        try {
          realPage = await real.cases.list(s, filters, cursor);
        } catch (e) {
          warn('GET cases', e);
        }
        return mergeCasePage(demoCases(await rows(s)), filters, realPage, !cursor);
      },
      get: async (s, cardId, id) => {
        const hit = isDemoCaseId(id) ? demoCases(await rows(s)).find((c) => c.id === id) : undefined;
        return hit ? structuredClone(hit) : real.cases.get(s, cardId, id);
      },
      precision: async (s, range = {}) => {
        let realReport: RulePrecisionReport = { cases: 0, rules: [] };
        try {
          realReport = await real.cases.precision(s, range);
        } catch (e) {
          warn('GET rule precision', e);
        }
        // The range comes as `…T00:00:00+07:00` (Bangkok days): compare instants, not strings.
        const [from, to] = [range.from ? Date.parse(range.from) : NaN, range.to ? Date.parse(range.to) : NaN];
        const inRange = demoCases(await rows(s)).filter((c) => {
          const at = Date.parse(c.decisionUtc);
          return (Number.isNaN(from) || at >= from) && (Number.isNaN(to) || at < to);
        });
        return mergePrecision(inRange, realReport);
      },
    },
  };

  return {
    ...real,
    decisions: wrapped.decisions,
    decisionsRange: wrapped.decisionsRange,
    stats: wrapped.stats,
    versions: wrapped.versions,
    version: wrapped.version,
    audit: wrapped.audit,
    masters: { ...real.masters, ...wrapped.masters },
    // R9 cases
    cases: { ...real.cases, ...wrapped.cases },
  };
}
